/**
 * Turn alerts, client side.
 *
 * - Push (phones, and desktops with the tab hidden): the player turns it on in Options; this
 *   browser subscribes with the server's key and the server pushes when the game has waited on
 *   them 15 s and no tab of theirs is on screen (`sw.js` shows it). iPhone/iPad only allow it
 *   from the Home Screen app. The subscription is re-saved on every sign-in (it follows whoever
 *   is signed in here) and dropped on sign out.
 * - Tab: while the game waits on you and this tab is hidden or out of focus, its title and
 *   favicon flash (`useTabAlert`).
 */
import { useEffect } from 'react';
import { t } from '@kadice/shared';
import { api } from './api';

export type PushState = 'off' | 'on' | 'unsupported' | 'serverOff' | 'denied' | 'install';

const NOTIFICATION_TAG = 'kadice-turn';
let pushKey: Promise<string | null> | null = null;
const serverKey = () =>
  (pushKey ??= api.config().then(
    (c) => c.pushKey,
    () => null,
  ));

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () =>
  matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Turn alerts in this browser: on, off, or why it can't be turned on. */
export async function pushState(): Promise<PushState> {
  if (isIos() && !standalone()) return 'install';
  if (!supported()) return 'unsupported';
  if (!(await serverKey())) return 'serverOff';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.permission === 'granted' && (await currentSubscription()) ? 'on' : 'off';
}

/** Ask permission (call straight from a tap), subscribe and save. */
export async function enablePush(): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const key = await serverKey();
  if (!key) return 'serverOff';
  await subscribe(key);
  return 'on';
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.deletePush(sub.endpoint).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

/** Signed in: this browser's alerts go to whoever is signed in now (and survive a server key change). */
export async function resyncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted' || !(await currentSubscription())) return;
  const key = await serverKey();
  if (key) await subscribe(key).catch(() => {});
}

async function subscribe(key: string): Promise<void> {
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const applicationServerKey = fromBase64Url(key);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options.applicationServerKey, applicationServerKey)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  await api.savePush(sub.toJSON());
}

/** Back on the game: clear its alerts from the notification tray. */
function clearNotifications(): void {
  if (!supported()) return;
  void navigator.serviceWorker
    .getRegistration()
    .then((reg) => reg?.getNotifications({ tag: NOTIFICATION_TAG }))
    .then((list) => list?.forEach((n) => n.close()))
    .catch(() => {});
}

const FLASH_MS = 1000;
let alertIcon: string | null = null;

/** The favicon's alert frame: a red dot. */
function alertIconUrl(): string {
  if (alertIcon) return alertIcon;
  const c = document.createElement('canvas');
  c.width = c.height = 32;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = '#e5484d';
    g.beginPath();
    g.arc(16, 16, 14, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#ffffff';
    g.beginPath();
    g.arc(16, 16, 5, 0, Math.PI * 2);
    g.fill();
  }
  return (alertIcon = c.toDataURL('image/png'));
}

/** While the game waits on you and this tab is hidden or out of focus, flash its title and favicon. */
export function useTabAlert(awaited: boolean): void {
  useEffect(() => {
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const baseIcon = icon?.href ?? '';
    const baseTitle = t('app.title');
    let timer: ReturnType<typeof setInterval> | null = null;
    let lit = false;

    const show = (on: boolean) => {
      lit = on;
      document.title = on ? t('tab.alert') : baseTitle;
      if (icon) icon.href = on ? alertIconUrl() : baseIcon;
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
      show(false);
    };
    const update = () => {
      const away = document.visibilityState === 'hidden' || !document.hasFocus();
      if (!away) clearNotifications();
      if (!awaited || !away) return stop();
      if (!timer) {
        show(true);
        timer = setInterval(() => show(!lit), FLASH_MS);
      }
    };

    update();
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
      stop();
    };
  }, [awaited]);
}

function fromBase64Url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = (s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function sameKey(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.byteLength) return false;
  const x = new Uint8Array(a);
  return x.every((v, i) => v === b[i]);
}
